import { Bot } from "../../../models";
import { WhatsAppIncomingCall } from "../../../models/WhatsAppCall";

interface WebRTCCallbacks {
  onConnectionStateChange: (state: RTCIceConnectionState) => void;
  onCallEnded: () => void;
}

export class WhatsAppWebRTCService {
  private peerConnection: RTCPeerConnection | null = null;
  private localStream: MediaStream | null = null;
  private apiBaseUrl = process.env.REACT_APP_CHATHUB_APP_URL || "";
  private currentCallId: string | null = null;
  private bot: Bot;

  constructor(bot: Bot) {
    this.bot = bot;
    this.setupPeerConnection = this.setupPeerConnection.bind(this);
    this.cleanup = this.cleanup.bind(this);
  }

  async acceptCall(
    incomingCall: WhatsAppIncomingCall,
    callbacks: WebRTCCallbacks
  ): Promise<void> {
    try {
      this.currentCallId = incomingCall.callId;

      // Setup peer connection
      await this.setupPeerConnection(callbacks);

      // Get user media
      this.localStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 48000,
          channelCount: 1,
        },
      });

      // Add local stream to peer connection
      this.localStream.getTracks().forEach((track) => {
        this.peerConnection!.addTrack(track, this.localStream!);
      });

      // Set remote description (SDP offer)
      await this.peerConnection!.setRemoteDescription(
        new RTCSessionDescription({
          type: "offer",
          sdp: this.sanitizeSdp(incomingCall.sdpOffer),
        })
      );

      // Create and set local description (SDP answer)
      const answer = await this.peerConnection!.createAnswer({
        offerToReceiveAudio: true,
        voiceActivityDetection: false,
      });

      const enhancedSdp = this.enhanceSdpForInternational(answer.sdp!);
      const enhancedAnswer = new RTCSessionDescription({
        type: "answer",
        sdp: enhancedSdp,
      });

      await this.peerConnection!.setLocalDescription(enhancedAnswer);

      // Wait for ICE gathering
      await this.waitForIceGathering();

      // Send SDP answer to backend
      await this.sendSdpAnswer(
        incomingCall.callId,
        this.peerConnection!.localDescription!.sdp
      );
    } catch (error) {
      console.error("Error accepting call:", error);
      this.cleanup();
      throw error;
    }
  }

  async rejectCall(callId: string): Promise<void> {
    const response = await fetch(
      `${this.apiBaseUrl}/api/${this.bot.id}/whatsapp-calls/${callId}/reject`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key: this.bot.key,
          password: this.bot.password,
        }),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to reject call: ${response.statusText}`);
    }
  }

  async terminateCall(callId?: string): Promise<void> {
    const targetCallId = callId || this.currentCallId;
    if (!targetCallId) return;

    try {
      const response = await fetch(
        `${this.apiBaseUrl}/api/${this.bot.id}/whatsapp-calls/${targetCallId}/terminate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            key: this.bot.key,
            password: this.bot.password,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to terminate call: ${response.statusText}`);
      }
    } finally {
      this.cleanup();
    }
  }

  cleanup(): void {
    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
    }

    if (this.localStream) {
      this.localStream.getTracks().forEach((track) => track.stop());
      this.localStream = null;
    }

    this.currentCallId = null;
  }

  private async setupPeerConnection(callbacks: WebRTCCallbacks): Promise<void> {
    this.peerConnection = new RTCPeerConnection({
      iceServers: [
        { urls: "stun:stun.l.google.com:19302" },
        { urls: "stun:stun1.l.google.com:19302" },
        { urls: "stun:stun2.l.google.com:19302" },
        {
          urls: "turn:openrelay.metered.ca:80",
          username: "openrelayproject",
          credential: "openrelayproject",
        },
        {
          urls: "turn:openrelay.metered.ca:443",
          username: "openrelayproject",
          credential: "openrelayproject",
        },
      ],
      iceCandidatePoolSize: 10,
      bundlePolicy: "max-bundle",
      rtcpMuxPolicy: "require",
    });

    // Handle connection state changes
    this.peerConnection.oniceconnectionstatechange = () => {
      const state = this.peerConnection!.iceConnectionState;
      callbacks.onConnectionStateChange(state);

      if (
        state === "failed" ||
        state === "closed" ||
        state === "disconnected"
      ) {
        callbacks.onCallEnded();
      }
    };

    // Handle remote stream
    this.peerConnection.ontrack = (event) => {
      const remoteAudio = document.getElementById(
        "whatsapp-remote-audio"
      ) as HTMLAudioElement;
      if (remoteAudio && event.streams[0]) {
        remoteAudio.srcObject = event.streams[0];
        remoteAudio.play().catch(console.error);
      }
    };
  }

  private sanitizeSdp(sdp: string): string {
    return (
      sdp
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter((line) => line.length > 0)
        .join("\r\n") + "\r\n"
    );
  }

  private enhanceSdpForInternational(sdp: string): string {
    let lines = sdp.split("\r\n");

    for (let i = 0; i < lines.length; i++) {
      // Enhance Opus configuration for international calls
      if (lines[i].includes("a=fmtp:111") && lines[i].includes("opus")) {
        lines[i] =
          "a=fmtp:111 minptime=10;maxaveragebitrate=32000;maxplaybackrate=48000;sprop-maxcapturerate=48000;useinbandfec=1;usedtx=1";
      }

      // Add jitter buffer configuration
      if (lines[i].startsWith("m=audio")) {
        lines.splice(i + 1, 0, "a=rtcp-fb:* goog-remb");
        lines.splice(i + 2, 0, "a=rtcp-fb:* ccm fir");
        i += 2;
      }
    }

    return lines.join("\r\n");
  }

  private async waitForIceGathering(): Promise<void> {
    return new Promise((resolve) => {
      const timeout = setTimeout(() => resolve(), 30000); // 30s timeout

      this.peerConnection!.onicecandidate = (event) => {
        if (!event.candidate) {
          clearTimeout(timeout);
          resolve();
        }
      };
    });
  }

  private async sendSdpAnswer(callId: string, sdp: string): Promise<void> {
    const response = await fetch(
      `${this.apiBaseUrl}/api/${this.bot.id}/whatsapp-calls/${callId}/accept`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          sdp,
          sdp_type: "answer",
          key: this.bot.key,
          password: this.bot.password,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to send SDP answer");
    }
  }
}
